import { useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useHookFormMask } from 'use-mask-input'
import { CatchMessageError } from '../../remote/catch-message-error'
import { ComboSelectWithLabel } from '../components/combo-select-with-label'
import { CustomSelect } from '../components/custom-select'
import { Button, Input } from '../components/ui/components'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { useToast } from '../hooks'
import { applyMaskBrlCurrencyFloat, formatCurrencyToNumber } from '../utils/formatCurrency'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { formatPhone, removeNonNumeric } from '../utils/formatPhone'
import { EmployeeFormData, employeeSchema } from '../validators/employee-form-validator'
import DateInputWithMask from '../components/date-input'


interface EmployeeFormProps {
  data?: any
  closeDialog?: () => void
  updateList?: () => void
}

export default function EmployeeForm({ data, closeDialog, updateList }: EmployeeFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  //const { remoteClientGet } = useRemoteClientGet()
  const { getUnitId } = useLocalStorage()
  const { showToast } = useToast()
  const isEditMode = Boolean(data?.id)
  //const [dataUnits, setDataUnits] = useState<any>([])
  const [perfis, setPerfis] = useState<any>()
  // console.log(data)
  const [users, setUsers] = useState<any>([])

  useEffect(() => {
    httpClientPost('/usuarios/filtro?page=0&size=1000', {
      perfis: [1, 3, 4, 5, 6, 7, 8, 9, 10],
      idUnidade: [getUnitId()]
    })
      .then((response: any) => {
        setUsers(response.data.data.content.map((item: any) => ({ ...item, label: item.nome, value: item.id.toString() })))
      })
      .catch(CatchMessageError)
  }, [])

  const form = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      id: data?.id || 0,
      cargo: data?.cargo || '',
      cpf: data?.cpf || '',
      ctps: data?.ctps || '00000000000',
      dataAdmissao: convertDateDisplayFormat(data?.dataAdmissao.split('T')[0] || ''),
      dataNascimento: convertDateDisplayFormat(data?.dataNascimento.split('T')[0] || ''),
      nome: data?.nome || '',
      nomeEmergencia: data?.nomeEmergencia || '',
      rg: data?.rg || '',
      salario: applyMaskBrlCurrencyFloat(data?.salario || '') || '',
      contato: {
        telefone: formatPhone(data?.contato?.telefone) || '',
        email: data?.contato.email || ''
      },
      telefoneEmergencia: data?.telefoneEmergencia || '',
      regimeTrabalho: 'CLT',
      tipoContrato: data?.tipoContrato || 'CLT',
      unidade: {
        id: data?.unidade?.id || getUnitId() || 0
      },
      idUsuario: data?.idUsuario + '' || ''
    }
  })

  // const fetchUnits = async () => {
  //   try {
  //     const response = await remoteClientGet(Endpoints.units, formatParams({
  //       page: 0,
  //       size: 1000,
  //       sortParam: 'id',
  //       order: 'desc',
  //       idUnidade: getUnitId()
  //     }));
  //     const data = response?.data?.data?.content;
  //     console.log(data)
  //     setDataUnits(data.map((unit: any) => ({
  //       ...unit,
  //       label: unit?.nomeFantasia,
  //       value: unit?.id
  //     })));
  //     console.log(dataUnits)
  //   } catch (error) {
  //     console.error(error);
  //     throw error
  //   }
  // }

  // useEffect(() => {
  //   console.log('fetching units')
  //   fetchUnits();
  // }, []);

  const fetchPerfil = async () => {
    setPerfis([])

    try {
      const response = await httpClientPost(`${Endpoints.perfis}/filtro?page=${0}&size=${100}&order=desc&sort=id`, {
        codigos: ['CAIXA', 'ADMINISTRADOR', 'COMERCIAL', 'CONSULTOR', 'COORDENADOR', 'PROFESSOR', 'PERSONAL']
      })

      const data = response?.data?.data?.content || []
      setPerfis(
        data.map((elem: any) => ({
          ...elem,
          label: elem.descricao,
          value: elem.codigo
        }))
      )
      console.log(data)
    } catch (error) {
      console.error('Erro ao buscar dados de serviços:', error)
    }
  }

  const onSubmit = async (formData: EmployeeFormData) => {
    
    console.log('dataNascimento', formData.dataNascimento)

    const formattedData = {
      ...formData,
      contato: {
        telefone: removeNonNumeric(formData.contato.telefone),
        email: formData.contato.email
      },
      ctps: formData.ctps.replace(/\D/g, ''),
      cpf: formData.cpf.replace(/\D/g, ''),
      dataNascimento: formData.dataNascimento,
      dataAdmissao: formData.dataAdmissao,
      salario: formatCurrencyToNumber(formData.salario),
      telefoneEmergencia: removeNonNumeric(formData.telefoneEmergencia)
    }

    try {
      if (isEditMode) {
        formattedData.inclusao = new Date().toDateString()
        await httpClientPut(Endpoints.employee, { ...formattedData })
        closeDialog?.()
        updateList?.()
      } else {
        delete formattedData.id
        await httpClientPost(Endpoints.employee, { ...formattedData })
      }
      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
      form.setValue('ctps', '00000000000')
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const registerWithMask = useHookFormMask(form.register)

  useEffect(() => {
    fetchPerfil()
  }, [])

  return (
    <Form {...form}>
      <form id="formEmployee" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 p-4">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="text-gray-400">*</span>
                Nome Completo
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nome completo" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="idUsuario"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {' '}
                <span className="text-gray-400">*</span> Usuário
              </FormLabel>
              <FormControl>
                <ComboSelectWithLabel
                  defaultValue={field.value}
                  placeholder="Selecione um usuário"
                  data={users}
                  onChange={(e) => {
                    console.log(e)
                    field.onChange(e?.value)
                  }}
                />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors?.idUsuario?.message}</FormMessage>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> CPF/CNPJ
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('cpf', ['999.999.999-99', '99.999.999/9999-99'])} placeholder="Informe o CPF ou CNPJ" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.cpf?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dataNascimento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Data de Nascimento
                </FormLabel>
                <FormControl>
                   <DateInputWithMask value={field.value} onChange={field.onChange} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataNascimento?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="rg"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> RG
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="RG" {...registerWithMask('rg', ['99999999'])} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.rg?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contato.telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Telefone
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('contato.telefone', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="(00) [0]0000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.contato?.telefone?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        {/* 
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>  <
              span className="text-gray-400">*</span> Endereço completo</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Endereço completo" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.fullName?.message}</FormMessage>
            </FormItem>
          )}
        /> */}
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="ctps"
            render={({ field }) => (
              <FormItem>
                <FormLabel> CTPS</FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('ctps', '999.99999.99-9')} placeholder='"999.99999.99-9"' />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.ctps?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contato.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Email
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Email" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.contato?.email?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="cargo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Cargo
                </FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={perfis}
                    onChange={(e) => {
                      console.log(e)
                      form.setValue('cargo', e?.value)
                      form.trigger('cargo')
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.cargo?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dataAdmissao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Data de admissão
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dataAdmissao', '99/99/9999')} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataAdmissao?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="nomeEmergencia"
            render={({ field }) => (
              <FormItem>
                <FormLabel> Nome contato de emergência</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome contato de emergência" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.nomeEmergencia?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefoneEmergencia"
            render={({ field }) => (
              <FormItem>
                <FormLabel> Contato de emergência</FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('telefoneEmergencia', ['(99) 9999-9999', '(99) 99999-9999'])} placeholder="(00) [0]0000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.telefoneEmergencia?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="tipoContrato"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Tipo de contrato
                </FormLabel>
                <CustomSelect
                  onValueChange={(data) => {
                    field.onChange(data)
                    form.setValue('regimeTrabalho', data)
                  }}
                  defaultValue={field.value ?? ''}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione contrato" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="PJ">PJ</SelectItem>
                    <SelectItem value="CLT">CLT</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.tipoContrato?.message}</FormMessage>
              </FormItem>
            )}
          />
          {/* <FormField
            control={form.control}
            name="tipoContrato"
            render={({ field }) => (
              <FormItem>
                <FormLabel>  <
                  span className="text-gray-400">*</span> Regime de trabalho</FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value ?? ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione regime" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Presencial">Presencial</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.regimeTrabalho?.message}</FormMessage>
              </FormItem>
            )}
          /> */}
          {/* <FormField
            control={form.control}
            name="unidade.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Unidade</FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={dataUnits}
                    onChange={(e) => {
                      //console.log(e)
                      const id = e?.value;
                      form.setValue('unidade.id', id);
                      form.trigger(['unidade.id']);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.unidade?.message}</FormMessage>
              </FormItem>
            )}
          /> */}
          <FormField
            control={form.control}
            name="salario"
            render={({ field: { ref, ...field } }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Salario
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="R$ "
                    decimalScale={2}
                    getInputRef={ref}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.salario?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        {!isEditMode && (
          <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-50 mt-3 ">
            Enviar
          </Button>
        )}
      </form>
    </Form>
  )
}
